import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { KeyRound, RefreshCw, Send, Mail, Phone, AlertTriangle } from 'lucide-react';
import { adminApi } from '../api';
import { toast } from 'sonner';
import PostCreateClientDialog from './PostCreateClientDialog';

const genPass = () => Math.random().toString(36).slice(2, 10);

/**
 * Dialog to (re-)send credentials to an existing client.
 * Flow:
 *  1) Admin sets/generates a new password (existing password is never plain-text retrievable)
 *  2) Optionally sends the email via SMTP
 *  3) Opens the PostCreateClientDialog with the plain password + wa_link for manual WhatsApp send
 */
const SendCredentialsDialog = ({ open, onOpenChange, client, onSaved }) => {
  const [password, setPassword] = useState(genPass());
  const [sendEmail, setSendEmail] = useState(!!client?.email);
  const [busy, setBusy] = useState(false);
  const [postOpen, setPostOpen] = useState(false);
  const [postData, setPostData] = useState(null);

  React.useEffect(() => {
    if (open) {
      setPassword(genPass());
      setSendEmail(!!client?.email);
    }
  }, [open, client]);

  if (!client) return null;

  const submit = async () => {
    if (!password.trim()) return toast.error('Password obbligatoria');
    setBusy(true);
    try {
      // Update password on the client
      await adminApi.updateClient(client.id, { password: password.trim() });

      // Send email if requested
      let emailSent = false, emailError = null;
      if (sendEmail && client.email) {
        try {
          const res = await adminApi.sendClientCredentials(client.id, { channels: ['email'], password: password.trim() });
          emailSent = !!res.email_sent;
          emailError = res.email_error || null;
        } catch (e) { emailError = 'Errore invio email'; }
      }

      // Build wa_link locally
      let waLink = '';
      if (client.phone_wa) {
        const digits = client.phone_wa.replace(/\D/g, '');
        if (digits) {
          const link = `${window.location.origin}/c/${client.slug}`;
          const loginId = client.email || client.name;
          const msg = `Ciao ${client.name},\n\nLe tue foto sono pronte nella tua area riservata FRAMEBOX by Nicola Verardo.\n\nLink: ${link}\nNome: ${loginId}\nPassword: ${password}\n\nA presto,\nNicola`;
          waLink = `https://wa.me/${digits}?text=${encodeURIComponent(msg)}`;
        }
      }

      toast.success('Password aggiornata');
      setPostData({
        client: { ...client, __password: password },
        waLink, emailSent, emailError,
      });
      onOpenChange(false);
      setPostOpen(true);
      onSaved && await onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore invio');
    } finally { setBusy(false); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <div className="flex items-center gap-2 text-[#B8944B] mb-1">
              <Send className="w-4 h-4" />
              <span className="text-xs font-bold uppercase tracking-widest">Invia credenziali</span>
            </div>
            <DialogTitle className="font-display text-2xl">A: {client.name}</DialogTitle>
            <DialogDescription>
              Per motivi di sicurezza la password attuale non è recuperabile.
              Genera una nuova password: sostituirà quella precedente e verrà inviata al cliente.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-3 space-y-3">
            {/* Client contacts summary */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2 flex items-center gap-2">
                <Mail className={`w-3.5 h-3.5 ${client.email ? 'text-slate-600' : 'text-slate-300'}`} />
                <span className={`truncate ${client.email ? 'text-slate-700' : 'text-slate-400 italic'}`}>{client.email || 'nessuna email'}</span>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2 flex items-center gap-2">
                <Phone className={`w-3.5 h-3.5 ${client.phone_wa ? 'text-emerald-600' : 'text-slate-300'}`} />
                <span className={`truncate font-mono ${client.phone_wa ? 'text-slate-700' : 'text-slate-400 italic'}`}>{client.phone_wa || 'nessun WhatsApp'}</span>
              </div>
            </div>

            <label className="block">
              <span className="text-sm font-medium text-slate-700">Nuova password</span>
              <div className="mt-1 flex items-center gap-2 border border-slate-200 rounded-xl px-3 py-2.5 focus-within:border-[#C9A961] focus-within:ring-2 focus-within:ring-[#F7F1E1]">
                <KeyRound className="w-4 h-4 text-slate-400" />
                <input type="text" autoComplete="off" data-testid="send-cred-password" value={password} onChange={e=>setPassword(e.target.value)} className="flex-1 outline-none bg-transparent font-mono text-slate-900" />
                <button type="button" onClick={()=>setPassword(genPass())} className="text-slate-500 hover:text-[#B8944B] p-1" title="Rigenera"><RefreshCw className="w-4 h-4" /></button>
              </div>
              <span className="block text-[11px] text-slate-500 mt-1">La password è visibile in chiaro qui — verrà inviata al cliente esattamente come la vedi.</span>
            </label>

            <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>Cliccando su "Continua" la password del cliente verrà <b>sostituita</b> con quella qui sopra.</span>
            </div>

            {client.email && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" data-testid="send-cred-email-check" checked={sendEmail} onChange={e=>setSendEmail(e.target.checked)} className="accent-[#C9A961] w-4 h-4" />
                <Mail className="w-4 h-4 text-slate-500" />
                <span className="text-sm text-slate-700">Invia anche via email a <b>{client.email}</b></span>
              </label>
            )}
          </div>

          <div className="mt-4 flex items-center justify-end gap-2">
            <button type="button" onClick={()=>onOpenChange(false)} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Annulla</button>
            <button data-testid="send-cred-submit" disabled={busy} onClick={submit} className="inline-flex items-center gap-2 bg-black hover:bg-[#C9A961] hover:text-black text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors">
              <Send className="w-4 h-4" /> {busy ? 'Attendere…' : 'Continua'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <PostCreateClientDialog
        open={postOpen}
        onOpenChange={setPostOpen}
        client={postData?.client}
        waLink={postData?.waLink}
        emailSent={postData?.emailSent}
        emailError={postData?.emailError}
      />
    </>
  );
};

export default SendCredentialsDialog;
